import type { DatasetCartesianEditorProps } from "./dataset-cartesian-editor.type";
import type { DatasetCartesian } from "@/entities/dataset";

import { Space, Empty } from "antd";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DATASET_TYPE } from "@/entities/dataset";
import { useDatasetsMutatorSync } from "@/modules/datasets/datasets-mutator";
import { PositionMarker } from "@/ui/position-marker";

import {
  formatMarkerData,
  getClickMarkerStyle,
  getDefaultMarkerStyle,
  getImageUrl,
} from "./dataset-cartesian-editor.helper";

import "./dataset-cartesian-editor.style.less";

const datasetType = DATASET_TYPE.CARTESIAN;

export const DatasetCartesianEditor = ({
  disabled = false,
  template,
  dataset,
  asset,
}: DatasetCartesianEditorProps) => {
  const { t } = useTranslation();

  const clickableArea = useRef<HTMLDivElement>(null);

  const { addMutatorItem } = useDatasetsMutatorSync();

  const imageUrl = getImageUrl(dataset, asset);

  const defaultMarkerStyle = getDefaultMarkerStyle(dataset);

  const [datasetState, setDatasetState] = useState<
    | {
        markerStyle?: { left: string; top: string; depth?: string };
      }
    | undefined
  >({ markerStyle: defaultMarkerStyle });

  /**
   * Attempts to update or creeate the dataset.
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = clickableArea.current?.getBoundingClientRect();
    if (!rect) return;

    const coordinate_x = (event.clientX - rect.left) / rect.width;
    const coordinate_y = (event.clientY - rect.top) / rect.height;
    const coordinate_z = 0;

    const coordinates = formatMarkerData(
      coordinate_x,
      coordinate_y,
      coordinate_z
    );

    const clickMarkerStyle = getClickMarkerStyle({
      coordinate_x,
      coordinate_y,
      coordinate_z,
    });

    setDatasetState({ markerStyle: clickMarkerStyle });

    if (dataset) {
      const variables: Partial<DatasetCartesian> = {
        data: coordinates,
      };
      addMutatorItem({
        mutationType: "update",
        datasetType,
        datasetId: dataset.id,
        variables,
      });
    } else {
      // create dataset
      const variables: Partial<DatasetCartesian> = {
        name: template.name ?? "",
        type: datasetType,
        data: coordinates,
        source: asset?.cartesian_image?.path,
        layout_index: template.layout_index,
      };
      addMutatorItem({
        mutationType: "create",
        datasetType,
        variables,
      });
    }
  };

  return (
    <div className="dataset-cartesian-editor">
      {imageUrl ? (
        <Space orientation="vertical" size="middle">
          <div
            ref={clickableArea}
            role="button"
            aria-label={t("dataset:cartesian.clickableAreaLabel")}
            tabIndex={disabled ? -1 : 0}
            onClick={!disabled ? handleClick : undefined}
            className={classNames("dataset-cartesian-clickable-area", {
              "-disabled": disabled,
            })}
          >
            <img
              src={imageUrl}
              className="dataset-cartesian-image"
              alt={t("dataset:cartesian.imageTitle")}
            />
            {datasetState?.markerStyle && (
              <PositionMarker style={datasetState?.markerStyle} />
            )}
          </div>
        </Space>
      ) : (
        <Empty description={t("global:noData")} />
      )}
    </div>
  );
};
